import React, { useEffect, useState, useRef, useCallback, memo } from 'react';
import { normalizeFacePinUserId, resolveFacePinStreamForUserId } from './ivsFaceVideo';
import {
  normalizeVideoFaceLayoutFromUnityPayload,
  type FaceLayoutEntry,
} from './facePinViewportProjection';

export type { FaceLayoutEntry };

type Props = {
  faceStreamsByUserId: Record<string, MediaStream>;
  /** Kept for API compatibility; local user is shown when a stream exists (see IVS panel local clone). */
  localUserId: string;
  /** When the local user's pin is visible, clicking it opens the Video dock (e.g. turn camera off). */
  onLocalFacePinClick?: () => void;
};

const MAX_FACE_PINS = 10;
const LERP = 0.34;
const SNAP_EPS = 1e-5;
/** Rounded viewport units — skip work when Unity repeats an effectively identical payload. */
const LAYOUT_SIG_MUL = 2500;

function streamAttachSig(keys: string[], streams: Record<string, MediaStream>): string {
  return keys
    .map((k) => {
      const s = streams[k];
      const t = s?.getVideoTracks?.()?.[0];
      return `${k}:${t?.id ?? ''}:${t?.readyState ?? ''}`;
    })
    .join('|');
}

type LayoutTarget = { vx: number; vy: number; vz: number; scale: number; visible: boolean };

function selectPinKeys(faces: FaceLayoutEntry[], streams: Record<string, MediaStream>): string[] {
  const candidates: { key: string; vz: number }[] = [];
  for (const f of faces) {
    if (!f?.userId || !f.visible) continue;
    const key = normalizeFacePinUserId(f.userId);
    if (!key) continue;
    if (!resolveFacePinStreamForUserId(streams, f.userId)) continue;
    candidates.push({ key, vz: f.vz });
  }
  candidates.sort((a, b) => a.vz - b.vz);
  return candidates.slice(0, MAX_FACE_PINS).map((c) => c.key);
}

function fillLayoutTargets(map: Map<string, LayoutTarget>, faces: FaceLayoutEntry[], keys: Set<string>): void {
  map.clear();
  for (const f of faces) {
    const key = normalizeFacePinUserId(f.userId);
    if (!key || !keys.has(key)) continue;
    map.set(key, {
      vx: f.vx,
      vy: f.vy,
      vz: f.vz,
      scale: f.scale,
      visible: f.visible,
    });
  }
}

function compactLayoutTargetSig(keys: string[], targets: Map<string, LayoutTarget>): string {
  let out = '';
  for (const k of keys) {
    const t = targets.get(k);
    if (!t) {
      out += `${k}:.;`;
      continue;
    }
    out += `${k}:${Math.round(t.vx * LAYOUT_SIG_MUL)}:${Math.round(t.vy * LAYOUT_SIG_MUL)}:${
      t.visible ? 1 : 0
    }:${Math.round(t.scale * 1000)}:${Math.round(t.vz * LAYOUT_SIG_MUL)};`;
  }
  return out;
}

/** Build targets only for signature compare — avoids mutating refs when Unity repeats the same layout. */
function peekCompactLayoutSig(faces: FaceLayoutEntry[], nextKeys: string[]): string {
  const set = new Set(nextKeys);
  const tmp = new Map<string, LayoutTarget>();
  for (const f of faces) {
    const key = normalizeFacePinUserId(f.userId);
    if (!key || !set.has(key)) continue;
    tmp.set(key, {
      vx: f.vx,
      vy: f.vy,
      vz: f.vz,
      scale: f.scale,
      visible: f.visible,
    });
  }
  return nextKeys.join('\u0001') + '\u0002' + compactLayoutTargetSig(nextKeys, tmp);
}

function keysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Screen-space mini videos over the Unity game area, positioned from Unity WorldToViewportPoint (VideoFaceLayout).
 * Hot path: layout targets in refs + rAF (stops when converged); React only re-renders when pin membership changes.
 */
const AvatarFacePinsInner: React.FC<Props> = ({ faceStreamsByUserId, localUserId, onLocalFacePinClick }) => {
  const [pinUserIds, setPinUserIds] = useState<string[]>([]);

  const videoRefs = useRef<Map<string, HTMLVideoElement | null>>(new Map());
  const wrapperRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
  const layoutTargetsRef = useRef<Map<string, LayoutTarget>>(new Map());
  const layoutDisplayRef = useRef<Map<string, { vx: number; vy: number }>>(new Map());
  const lastStreamAttachSigRef = useRef<string>('');
  const lastFacesRef = useRef<FaceLayoutEntry[]>([]);
  const streamsRef = useRef(faceStreamsByUserId);
  streamsRef.current = faceStreamsByUserId;

  const rafRef = useRef<number | null>(null);
  const pinUserIdsRef = useRef<string[]>([]);
  pinUserIdsRef.current = pinUserIds;
  const pinKeysJoinedRef = useRef('');
  const lastCompactLayoutSigRef = useRef('');
  const pendingLayoutDetailRef = useRef<unknown>(undefined);
  const layoutCoalesceRafRef = useRef<number | null>(null);

  const pruneVideoRefs = useCallback((activeKeys: Set<string>) => {
    for (const key of Array.from(videoRefs.current.keys())) {
      if (activeKeys.has(key)) continue;
      const el = videoRefs.current.get(key);
      if (el) {
        try {
          el.pause();
          el.srcObject = null;
        } catch {
          /* ignore */
        }
      }
      videoRefs.current.delete(key);
    }
    for (const key of Array.from(wrapperRefs.current.keys())) {
      if (activeKeys.has(key)) continue;
      wrapperRefs.current.delete(key);
    }
    for (const key of Array.from(layoutDisplayRef.current.keys())) {
      if (activeKeys.has(key)) continue;
      layoutDisplayRef.current.delete(key);
    }
  }, []);

  const stopRaf = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const runLayoutFrame = useCallback(() => {
    rafRef.current = null;
    const keys = pinUserIdsRef.current;
    if (keys.length === 0) {
      return;
    }
    let needsAnother = false;
    for (const key of keys) {
      const target = layoutTargetsRef.current.get(key);
      const wrap = wrapperRefs.current.get(key);
      if (!target || !wrap) {
        needsAnother = true;
        continue;
      }

      let disp = layoutDisplayRef.current.get(key);
      if (!disp) {
        disp = { vx: target.vx, vy: target.vy };
        layoutDisplayRef.current.set(key, disp);
      }

      const dx = target.vx - disp.vx;
      const dy = target.vy - disp.vy;
      if (Math.abs(dx) < SNAP_EPS && Math.abs(dy) < SNAP_EPS) {
        disp.vx = target.vx;
        disp.vy = target.vy;
        wrap.style.left = `${disp.vx * 100}%`;
        wrap.style.top = `${(1 - disp.vy) * 100}%`;
        wrap.style.willChange = '';
        continue;
      }

      needsAnother = true;
      disp.vx += dx * LERP;
      disp.vy += dy * LERP;
      wrap.style.left = `${disp.vx * 100}%`;
      wrap.style.top = `${(1 - disp.vy) * 100}%`;
      wrap.style.willChange = 'left, top';
    }
    if (needsAnother) {
      rafRef.current = requestAnimationFrame(runLayoutFrame);
    }
  }, []);

  const scheduleLayoutTick = useCallback(() => {
    if (rafRef.current == null && pinUserIdsRef.current.length > 0) {
      rafRef.current = requestAnimationFrame(runLayoutFrame);
    }
  }, [runLayoutFrame]);

  useEffect(() => {
    return () => {
      stopRaf();
    };
  }, [stopRaf]);

  useEffect(() => {
    const active = new Set(pinUserIds);
    pruneVideoRefs(active);
  }, [pinUserIds, pruneVideoRefs]);

  useEffect(() => {
    for (const key of pinUserIds) {
      const el = videoRefs.current.get(key);
      const stream = resolveFacePinStreamForUserId(faceStreamsByUserId, key);
      if (!el || !stream) continue;
      if (el.srcObject !== stream) {
        el.srcObject = stream;
        el.muted = true;
        el.playsInline = true;
        void el.play().catch(() => {});
      }
    }
  }, [faceStreamsByUserId, pinUserIds]);

  /** When IVS adds a stream, membership may change without a new Unity layout snapshot. */
  useEffect(() => {
    const faces = lastFacesRef.current;
    if (faces.length === 0) return;
    const nextKeys = selectPinKeys(faces, faceStreamsByUserId);
    const sig = streamAttachSig(nextKeys, faceStreamsByUserId);
    if (sig === lastStreamAttachSigRef.current && keysEqual(pinUserIdsRef.current, nextKeys)) {
      return;
    }
    lastStreamAttachSigRef.current = sig;
    const nextSet = new Set(nextKeys);
    fillLayoutTargets(layoutTargetsRef.current, faces, nextSet);
    pinKeysJoinedRef.current = nextKeys.join('\u0001');
    lastCompactLayoutSigRef.current =
      pinKeysJoinedRef.current + '\u0002' + compactLayoutTargetSig(nextKeys, layoutTargetsRef.current);
    setPinUserIds((prev) => {
      if (keysEqual(prev, nextKeys)) return prev;
      for (const key of nextKeys) {
        if (!layoutDisplayRef.current.has(key)) {
          const t = layoutTargetsRef.current.get(key);
          layoutDisplayRef.current.set(key, { vx: t?.vx ?? 0, vy: t?.vy ?? 0 });
        }
      }
      return nextKeys;
    });
    requestAnimationFrame(scheduleLayoutTick);
  }, [faceStreamsByUserId, scheduleLayoutTick]);

  useEffect(() => {
    const flushLayout = () => {
      layoutCoalesceRafRef.current = null;
      const raw = pendingLayoutDetailRef.current;
      pendingLayoutDetailRef.current = undefined;
      if (raw === undefined) {
        return;
      }

      let faces: FaceLayoutEntry[];
      try {
        const data = typeof raw === 'string' ? JSON.parse(raw as string) : raw;
        faces = normalizeVideoFaceLayoutFromUnityPayload(data);
      } catch {
        lastFacesRef.current = [];
        layoutTargetsRef.current.clear();
        pinKeysJoinedRef.current = '';
        lastCompactLayoutSigRef.current = '';
        setPinUserIds([]);
        return;
      }

      lastFacesRef.current = faces;
      const streams = streamsRef.current;
      const nextKeys = selectPinKeys(faces, streams);
      const compactSig = peekCompactLayoutSig(faces, nextKeys);
      if (compactSig === lastCompactLayoutSigRef.current) {
        return;
      }
      lastCompactLayoutSigRef.current = compactSig;

      const nextSet = new Set(nextKeys);
      fillLayoutTargets(layoutTargetsRef.current, faces, nextSet);

      const prevJoined = pinKeysJoinedRef.current;
      const nextJoined = nextKeys.join('\u0001');
      const keysChanged = prevJoined !== nextJoined;
      pinKeysJoinedRef.current = nextJoined;

      setPinUserIds((prev) => {
        if (keysEqual(prev, nextKeys)) {
          return prev;
        }
        for (const key of nextKeys) {
          if (!layoutDisplayRef.current.has(key)) {
            const t = layoutTargetsRef.current.get(key);
            layoutDisplayRef.current.set(key, { vx: t?.vx ?? 0, vy: t?.vy ?? 0 });
          }
        }
        return nextKeys;
      });

      const kickMotion = () => {
        scheduleLayoutTick();
      };

      if (keysChanged) {
        requestAnimationFrame(() => {
          for (const key of nextKeys) {
            const wrap = wrapperRefs.current.get(key);
            const disp = layoutDisplayRef.current.get(key);
            if (wrap && disp) {
              wrap.style.left = `${disp.vx * 100}%`;
              wrap.style.top = `${(1 - disp.vy) * 100}%`;
            }
          }
          kickMotion();
        });
      } else {
        kickMotion();
      }
    };

    const queueLayout = (ev: Event) => {
      try {
        pendingLayoutDetailRef.current = (ev as CustomEvent<string>).detail;
        if (layoutCoalesceRafRef.current == null) {
          layoutCoalesceRafRef.current = requestAnimationFrame(flushLayout);
        }
      } catch {
        lastFacesRef.current = [];
        layoutTargetsRef.current.clear();
        pinKeysJoinedRef.current = '';
        lastCompactLayoutSigRef.current = '';
        setPinUserIds([]);
      }
    };

    window.addEventListener('VideoFaceLayout', queueLayout as EventListener);
    return () => {
      window.removeEventListener('VideoFaceLayout', queueLayout as EventListener);
      if (layoutCoalesceRafRef.current != null) {
        cancelAnimationFrame(layoutCoalesceRafRef.current);
        layoutCoalesceRafRef.current = null;
      }
      pendingLayoutDetailRef.current = undefined;
      lastFacesRef.current = [];
      layoutTargetsRef.current.clear();
      layoutDisplayRef.current.clear();
    };
  }, [scheduleLayoutTick]);

  useEffect(() => {
    if (pinUserIds.length === 0) {
      stopRaf();
    } else {
      scheduleLayoutTick();
    }
  }, [pinUserIds.length, scheduleLayoutTick, stopRaf]);

  const localNorm = normalizeFacePinUserId(localUserId);

  return (
    <>
      {pinUserIds.map((layoutKey) => {
        const stream = resolveFacePinStreamForUserId(faceStreamsByUserId, layoutKey);
        if (!stream) {
          return null;
        }
        const target = layoutTargetsRef.current.get(layoutKey);
        const scale = target?.scale ?? 0.11;
        const wPct = Math.min(Math.max(scale, 0.04) * 100, 11);
        const disp = layoutDisplayRef.current.get(layoutKey);
        const vx = disp?.vx ?? target?.vx ?? 0;
        const vy = disp?.vy ?? target?.vy ?? 0;
        const isInteractiveLocalPin = Boolean(
          onLocalFacePinClick && localNorm && layoutKey && layoutKey === localNorm
        );

        return (
          <div
            key={layoutKey}
            ref={(el) => {
              wrapperRefs.current.set(layoutKey, el);
            }}
            className={`absolute rounded-full overflow-hidden border border-white/30 shadow-lg bg-black${
              isInteractiveLocalPin ? ' cursor-pointer' : ''
            }`}
            style={{
              left: `${vx * 100}%`,
              top: `${(1 - vy) * 100}%`,
              transform: 'translate(-50%, -50%)',
              width: `${wPct}%`,
              maxWidth: 100,
              aspectRatio: '1',
              pointerEvents: isInteractiveLocalPin ? 'auto' : 'none',
            }}
            {...(isInteractiveLocalPin
              ? {
                  'data-ff-local-face-pin': '',
                  role: 'button' as const,
                  tabIndex: 0,
                  'aria-label': 'Open video controls',
                  onClick: (e: React.MouseEvent) => {
                    e.stopPropagation();
                    onLocalFacePinClick?.();
                  },
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      onLocalFacePinClick?.();
                    }
                  },
                }
              : {})}
          >
            <video
              ref={(el) => {
                if (el) {
                  videoRefs.current.set(layoutKey, el);
                  if (el.srcObject !== stream) {
                    el.srcObject = stream;
                    el.muted = true;
                    el.playsInline = true;
                  }
                } else {
                  videoRefs.current.delete(layoutKey);
                }
              }}
              autoPlay
              playsInline
              muted
              disablePictureInPicture
              className="h-full w-full object-cover"
            />
          </div>
        );
      })}
    </>
  );
};

export default memo(AvatarFacePinsInner, (prev, next) => {
  if (prev.faceStreamsByUserId !== next.faceStreamsByUserId) return false;
  if (prev.localUserId !== next.localUserId) return false;
  if (prev.onLocalFacePinClick !== next.onLocalFacePinClick) return false;
  return true;
});
